// Admin — Contracts (Phase 1C MVP)
// Single-page CRUD + issue + upload-signed for contract records.
// No wizard, no evidence pack UI, no audit-trail viewer.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { resolveAndIssueContract, refreshAndIssueContract } from "@/lib/contractIssuance";
import {
  Loader2, Plus, FileText, Download, Upload, RefreshCw, X,
  CheckCircle2, AlertTriangle, ExternalLink, Trash2,
} from "lucide-react";

const STATUS_STYLES = {
  draft:         { label: "Draft",       cls: "bg-stone-100 text-stone-700 border-stone-300" },
  pending_issue: { label: "Issuing…",    cls: "bg-amber-50 text-amber-800 border-amber-300" },
  issued:        { label: "Issued",      cls: "bg-emerald-50 text-emerald-800 border-emerald-300" },
  signed:        { label: "Signed",      cls: "bg-sky-50 text-sky-800 border-sky-300" },
  superseded:    { label: "Superseded",  cls: "bg-stone-50 text-stone-500 border-stone-200" },
  revoked:       { label: "Revoked",     cls: "bg-rose-50 text-rose-800 border-rose-300" },
  retired:       { label: "Retired",     cls: "bg-stone-50 text-stone-400 border-stone-200" },
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

export function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || { label: status || "?", cls: "bg-stone-100 text-stone-700 border-stone-300" };
  return (
    <span data-testid={`contract-status-${status}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border font-medium ${s.cls}`}>
      {s.label}
    </span>
  );
}

const contractRef = (c) => c.contract_reference
  || c.contract_variables?.values?.CONTRACT_REFERENCE?.value
  || `#${c.id.slice(0, 8)}`;

export default function AdminContractsPage() {
  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [franchisees, setFranchisees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [busyRow, setBusyRow] = useState(null);
  const uploadInputRef = useRef(null);
  const [uploadTargetCid, setUploadTargetCid] = useState(null);
  // Modal state for the "stale variables — refresh & retry" recovery
  // flow. When the /issue endpoint returns reason_code=
  // "stale_frozen_variables", we surface a proper in-app dialog with
  // the exact missing marker codes and a "Refresh and issue" button
  // instead of a browser alert() that leaks API instructions.
  const [refreshPrompt, setRefreshPrompt] = useState(null);
  // Modal state for render-engine invariant failures — surfaces the
  // exact failed_invariant, marker_code, page + render_job_id so the
  // admin can act on the specific problem in the template rather than
  // guessing from a generic error string.
  const [renderErrorPrompt, setRenderErrorPrompt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const [c, t, f] = await Promise.all([
        api.get("/admin/contracts"),
        api.get("/admin/contract-templates"),
        api.get("/franchisees", { params: { limit: 500 } }),
      ]);
      setRows(c.data.items || []);
      setTemplates((t.data.items || []).filter((x) => ["approved", "current"].includes(x.status)));
      setFranchisees(f.data.items || f.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const templateById = useMemo(() =>
    Object.fromEntries(templates.map((t) => [t.id, t])), [templates]);
  const franchiseeById = useMemo(() =>
    Object.fromEntries(franchisees.map((f) => [f.id, f])), [franchisees]);

  async function resolveAndIssue(cid) {
    setBusyRow(cid);
    try {
      const c = rows.find((r) => r.id === cid);
      const result = await resolveAndIssueContract(cid, {
        hasResolvedVariables: !!c?.contract_variables,
      });
      if (!result.ok) {
        if (result.kind === "stale_frozen_variables") {
          setRefreshPrompt({
            contractId: cid,
            missingCodes: result.missingMarkerCodes || [],
            message: result.message,
          });
          return;
        }
        if (result.kind === "render_invariant_failed") {
          setRenderErrorPrompt({
            contractId: cid,
            failedInvariant: result.failedInvariant,
            markerCode: result.markerCode,
            page: result.page,
            bbox: result.bbox,
            renderJobId: result.renderJobId,
            templateId: result.templateId,
            templateVersion: result.templateVersion,
            message: result.message,
          });
          return;
        }
        alert(result.message);
        return;
      }
      await load();
    } finally { setBusyRow(null); }
  }

  async function confirmRefreshAndIssue(reason) {
    if (!refreshPrompt) return;
    const cid = refreshPrompt.contractId;
    setBusyRow(cid);
    setRefreshPrompt(null);
    try {
      const result = await refreshAndIssueContract(cid, reason);
      if (!result.ok) {
        alert(result.message);
        return;
      }
      await load();
    } finally { setBusyRow(null); }
  }

  async function downloadDraftPreview(cid) {
    try {
      const resp = await api.post(
        `/admin/contracts/${cid}/preview-pdf`,
        null,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(
        new Blob([resp.data], { type: "application/pdf" }),
      );
      // Open in a new tab. The watermark is baked into every page and
      // the URL is a short-lived object URL that is revoked on unload.
      window.open(url, "_blank", "noopener");
      // Revoke after a beat so the new tab has time to grab the blob.
      setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      alert(`Preview failed: ${e?.response?.data?.detail || e.message}`);
    }
  }

  async function downloadPersonalised(cid) {
    try {
      const { data } = await api.get(`/admin/contracts/${cid}/personalised-pdf`);
      window.open(data.url, "_blank");
    } catch (e) {
      alert(`Download failed: ${e?.response?.data?.detail || e.message}`);
    }
  }

  async function downloadSigned(cid) {
    try {
      const { data } = await api.get(`/admin/contracts/${cid}/signed-pdf`);
      window.open(data.url, "_blank");
    } catch (e) {
      alert(`Download failed: ${e?.response?.data?.detail || e.message}`);
    }
  }

  async function revokeIssued(cid) {
    // Two-step confirmation — a stern default and a required reason so
    // admins can't blow away a franchisee's live contract in a click.
    const reason = window.prompt(
      "Revoke this issued contract?\n\n" +
      "The franchisee will no longer see it in their portal. This is " +
      "for contracts they've rejected or asked to change — signed " +
      "contracts can't be revoked.\n\n" +
      "Please give a short reason (visible in the audit trail):",
    );
    if (reason === null) return; // user cancelled
    setBusyRow(cid);
    try {
      await api.post(`/admin/contracts/${cid}/revoke`, { reason: reason.trim() });
      await load();
    } catch (e) {
      alert(`Revoke failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusyRow(null); }
  }

  async function forceDeleteContract(cid, status) {
    // Testing-only escape hatch. Real legal records must NEVER be
    // routed through this — the confirm dialog spells it out and the
    // backend audit trail keeps a record even after the row is gone.
    const confirm1 = window.confirm(
      "TESTING ONLY — hard-delete this contract?\n\n" +
      `Status: ${status}\n\n` +
      "This removes the row and the personalised/signed PDF from " +
      "storage. It cannot be undone. Do NOT use this on a real " +
      "franchisee's live contract."
    );
    if (!confirm1) return;
    const reason = window.prompt(
      "Please give a short reason for the force delete " +
      "(recorded to the audit log):",
      "Testing signature flow — clearing stale contract",
    );
    if (reason === null || !reason.trim()) return;
    setBusyRow(cid);
    try {
      await api.delete(`/admin/contracts/${cid}/force`, {
        params: { confirm: true, reason: reason.trim() },
      });
      await load();
    } catch (e) {
      alert(`Force delete failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusyRow(null); }
  }

  function openUploadPicker(cid) {
    setUploadTargetCid(cid);
    setTimeout(() => uploadInputRef.current?.click(), 0);
  }

  async function onUploadPicked(ev) {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file || !uploadTargetCid) return;
    const cid = uploadTargetCid;
    setBusyRow(cid);
    try {
      const fd = new FormData();
      fd.append("pdf", file);
      await api.post(`/admin/contracts/${cid}/upload-signed`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
    } catch (e) {
      alert(`Upload failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusyRow(null);
      setUploadTargetCid(null);
    }
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-contracts-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold" data-testid="contracts-heading">Contracts</h1>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border rounded-md bg-white hover:bg-stone-50"
            data-testid="contracts-refresh-btn">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          <button
            onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border rounded-md bg-emerald-600 text-white hover:bg-emerald-700 border-emerald-600"
            data-testid="contracts-new-btn">
            <Plus className="h-4 w-4" /> New contract
          </button>
        </div>
      </div>

      {err && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 text-sm rounded flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> {err}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-stone-500 py-16 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-stone-500 border border-dashed rounded-md" data-testid="contracts-empty">
          No contracts yet — click <em>New contract</em> above.
        </div>
      ) : (
        <div className="border rounded-lg bg-white overflow-hidden" data-testid="contracts-table">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b text-left text-xs uppercase text-stone-600">
              <tr>
                <th className="p-3">Status</th>
                <th className="p-3">Franchisee</th>
                <th className="p-3">Template</th>
                <th className="p-3">Monthly fee</th>
                <th className="p-3">Created</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const tpl = templateById[c.template_id];
                const fr = franchiseeById[c.franchisee_id];
                const isBusy = busyRow === c.id;
                return (
                  <tr key={c.id} className="border-b last:border-0 hover:bg-stone-50" data-testid={`contract-row-${c.id}`}>
                    <td className="p-3"><StatusPill status={c.status} /></td>
                    <td className="p-3">
                      {fr ? `${fr.first_name || ""} ${fr.last_name || ""}`.trim() || fr.organisation : c.franchisee_id?.slice(0, 8)}
                      {fr?.organisation && (
                        <div className="text-xs text-stone-500">{fr.organisation}</div>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="text-stone-900">{tpl?.name || c.template_id?.slice(0, 8)}</div>
                      <div className="text-xs text-stone-500">{tpl?.contract_type}</div>
                    </td>
                    <td className="p-3">{c.monthly_fee != null ? `£${Number(c.monthly_fee).toFixed(2)}` : "—"}</td>
                    <td className="p-3 text-xs text-stone-500">{fmt(c.created_at)}</td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {c.status === "draft" && (
                          <button
                            onClick={() => downloadDraftPreview(c.id)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border rounded bg-white hover:bg-stone-50"
                            data-testid={`contract-preview-btn-${c.id}`}
                            title="Render the draft with a PREVIEW watermark. Does not change status or make the contract visible to the franchisee.">
                            <FileText className="h-3 w-3" /> Preview
                          </button>
                        )}
                        {c.status === "draft" && (
                          <button
                            onClick={() => resolveAndIssue(c.id)}
                            disabled={isBusy}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border rounded bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                            data-testid={`contract-issue-btn-${c.id}`}>
                            {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                            Issue
                          </button>
                        )}
                        {(c.status === "issued" || c.status === "signed" || c.status === "superseded") && c.personalised_pdf_r2_key && (
                          <button
                            onClick={() => downloadPersonalised(c.id)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border rounded bg-white hover:bg-stone-50"
                            data-testid={`contract-download-btn-${c.id}`}>
                            <Download className="h-3 w-3" /> PDF
                          </button>
                        )}
                        {c.status === "issued" && c.signing_mode !== "dual_confirmation" && (
                          <button
                            onClick={() => openUploadPicker(c.id)}
                            disabled={isBusy}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border rounded bg-sky-600 border-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
                            data-testid={`contract-upload-signed-btn-${c.id}`}>
                            {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                            Upload signed
                          </button>
                        )}
                        {c.status === "issued" && (
                          <button
                            onClick={() => revokeIssued(c.id)}
                            disabled={isBusy}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border rounded bg-white text-rose-700 border-rose-300 hover:bg-rose-50 disabled:opacity-50"
                            data-testid={`contract-revoke-btn-${c.id}`}
                            title="Withdraw this issued contract from the franchisee's portal"
                          >
                            {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                            Revoke
                          </button>
                        )}
                        {c.status === "revoked" && (
                          <span
                            className="text-xs text-rose-700"
                            data-testid={`contract-revoked-note-${c.id}`}
                            title={c.revoke_reason || ""}
                          >
                            Revoked {c.revoked_at ? fmt(c.revoked_at) : ""}
                            {c.revoke_reason ? ` — ${c.revoke_reason}` : ""}
                          </span>
                        )}
                        {(c.status === "signed") && c.signed_pdf_r2_key && (
                          <button
                            onClick={() => downloadSigned(c.id)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border rounded bg-white hover:bg-stone-50"
                            data-testid={`contract-download-signed-btn-${c.id}`}>
                            <Download className="h-3 w-3" /> Signed
                          </button>
                        )}
                        {c.status === "superseded" && c.superseded_by_contract_id && (
                          <span className="text-xs text-stone-500" data-testid={`contract-superseded-note-${c.id}`}>
                            → {c.superseded_by_contract_id.slice(0, 8)}
                          </span>
                        )}
                        {c.status !== "draft" && (
                          <button
                            onClick={() => forceDeleteContract(c.id, c.status)}
                            disabled={isBusy}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border rounded bg-white text-rose-800 border-rose-400 hover:bg-rose-100 disabled:opacity-50"
                            data-testid={`contract-force-delete-btn-${c.id}`}
                            title="TESTING ONLY — hard-delete this contract and its stored PDFs. Do not use on real live contracts."
                          >
                            {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                            Delete (test)
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <input
        ref={uploadInputRef}
        type="file"
        accept="application/pdf"
        onChange={onUploadPicked}
        className="hidden"
        data-testid="contracts-hidden-upload-input"
      />

      {showNew && (
        <NewContractModal
          templates={templates}
          franchisees={franchisees}
          onClose={() => setShowNew(false)}
          onCreated={async () => { setShowNew(false); await load(); }}
        />
      )}

      {refreshPrompt && (
        <RefreshAndIssueModal
          missingCodes={refreshPrompt.missingCodes}
          message={refreshPrompt.message}
          onCancel={() => setRefreshPrompt(null)}
          onConfirm={confirmRefreshAndIssue}
        />
      )}

      {renderErrorPrompt && (
        <RenderErrorModal
          detail={renderErrorPrompt}
          onClose={() => setRenderErrorPrompt(null)}
        />
      )}
    </div>
  );
}

export function RenderErrorModal({ detail, onClose }) {
  const {
    failedInvariant, markerCode, page, bbox, renderJobId,
    templateId, templateVersion, message, valuePreview,
  } = detail;
  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      data-testid="render-error-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0 mt-0.5">
            <AlertTriangle className="h-5 w-5 text-rose-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-stone-900">Contract could not be issued</h2>
            <p className="mt-2 text-sm text-stone-700" data-testid="render-error-message">{message}</p>
            <dl className="mt-4 text-xs text-stone-600 grid grid-cols-3 gap-y-1 gap-x-3">
              {failedInvariant && (
                <>
                  <dt className="font-medium">Reason</dt>
                  <dd className="col-span-2 font-mono text-stone-800" data-testid="render-error-invariant">{failedInvariant}</dd>
                </>
              )}
              {markerCode && (
                <>
                  <dt className="font-medium">Marker</dt>
                  <dd className="col-span-2 font-mono text-stone-800" data-testid="render-error-marker">{markerCode}</dd>
                </>
              )}
              {valuePreview && (
                <>
                  <dt className="font-medium">Value</dt>
                  <dd className="col-span-2 font-mono text-stone-800 break-all" data-testid="render-error-value-preview">
                    &ldquo;{valuePreview}&rdquo;
                  </dd>
                </>
              )}
              {page != null && (
                <>
                  <dt className="font-medium">Page</dt>
                  <dd className="col-span-2 font-mono text-stone-800">{page}</dd>
                </>
              )}
              {Array.isArray(bbox) && bbox.length === 4 && (
                <>
                  <dt className="font-medium">Bbox</dt>
                  <dd className="col-span-2 font-mono text-stone-800 truncate">
                    [{bbox.map((n) => Number(n).toFixed(1)).join(", ")}]
                  </dd>
                </>
              )}
              {templateId && (
                <>
                  <dt className="font-medium">Template</dt>
                  <dd className="col-span-2 font-mono text-stone-800 truncate">
                    {templateId}{templateVersion ? ` v${templateVersion}` : ""}
                  </dd>
                </>
              )}
              {renderJobId && (
                <>
                  <dt className="font-medium">Job</dt>
                  <dd className="col-span-2 font-mono text-stone-800 truncate">{renderJobId}</dd>
                </>
              )}
            </dl>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm border border-stone-300 rounded bg-white hover:bg-stone-50"
            data-testid="render-error-close"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function RefreshAndIssueModal({ missingCodes, message, onCancel, onConfirm }) {
  const [reason, setReason] = useState(
    "Template changed after initial variable freeze; refreshing before issue",
  );
  const [submitting, setSubmitting] = useState(false);
  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      data-testid="refresh-and-issue-modal"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0 mt-0.5">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-stone-900">Refresh required before issue</h2>
            <p className="mt-2 text-sm text-stone-700">{message}</p>
            {missingCodes && missingCodes.length > 0 && (
              <div className="mt-3 text-xs text-stone-600">
                <div className="uppercase tracking-wider font-bold text-[10px] mb-1">
                  Missing marker{missingCodes.length === 1 ? "" : "s"}
                </div>
                <div
                  className="flex flex-wrap gap-1"
                  data-testid="refresh-missing-codes"
                >
                  {missingCodes.map((code) => (
                    <code
                      key={code}
                      className="px-1.5 py-0.5 rounded bg-stone-100 border border-stone-300 text-stone-800"
                    >
                      {code}
                    </code>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <label className="block mt-4 text-sm">
          <span className="text-xs font-medium text-stone-700">Reason (recorded in the audit trail)</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            className="mt-1 w-full text-sm border border-stone-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            data-testid="refresh-reason-input"
          />
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-3 py-1.5 text-sm border border-stone-300 rounded bg-white hover:bg-stone-50 disabled:opacity-50"
            data-testid="refresh-and-issue-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={async () => {
              setSubmitting(true);
              await onConfirm(reason);
            }}
            disabled={submitting || !reason.trim()}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-emerald-600 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            data-testid="refresh-and-issue-confirm"
          >
            {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Refresh and issue
          </button>
        </div>
      </div>
    </div>
  );
}

export function NewContractModal({ templates, franchisees, onClose, onCreated, lockedFranchiseeId, renewalOf }) {
  const [templateId, setTemplateId] = useState(templates[0]?.id || "");
  const [franchiseeId, setFranchiseeId] = useState(lockedFranchiseeId || "");
  const [monthlyFee, setMonthlyFee] = useState("");
  const lockedFr = lockedFranchiseeId
    ? franchisees.find((f) => f.id === lockedFranchiseeId)
    : null;

  // ---------------------------------------------------------------
  // Renewal-template auto-detection.
  //
  // A renewal template (``contract_type`` ending in ``_renewal``) MUST
  // be tied to a predecessor contract before it can be saved —
  // otherwise HQ can end up with a "Franchise Renewal Agreement" PDF
  // that isn't actually superseding anything, resulting in an invalid
  // hybrid record (looks like a renewal, behaves like a new contract).
  //
  // Behaviour:
  //   * Parent may inject ``renewalOf`` — used by the franchisee
  //     detail page where the newest issued/signed contract for that
  //     franchisee is already known.
  //   * If no external ``renewalOf`` and the selected template is a
  //     renewal type, we auto-fetch the franchisee's contracts and
  //     surface a picker (auto-pre-selecting the newest supersedable
  //     candidate).
  //   * If a renewal template is picked but the franchisee has no
  //     supersedable predecessor at all, save is blocked with a
  //     clear error message.
  // ---------------------------------------------------------------
  const selectedTemplate = templates.find((t) => t.id === templateId) || null;
  const isRenewalTemplate = Boolean(
    selectedTemplate?.contract_type && /_renewal$/i.test(selectedTemplate.contract_type),
  );
  const [franchiseeCandidates, setFranchiseeCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [pickedPredecessorId, setPickedPredecessorId] = useState(null);

  // Fetch the franchisee's supersedable contracts whenever a renewal
  // template is selected against a franchisee, and we don't already
  // have an external ``renewalOf``. Silent on failure — the save-gate
  // below will still block the user with a friendly message.
  useEffect(() => {
    if (renewalOf) return; // external override wins
    if (!isRenewalTemplate) { setFranchiseeCandidates([]); setPickedPredecessorId(null); return; }
    if (!franchiseeId) { setFranchiseeCandidates([]); setPickedPredecessorId(null); return; }
    let cancelled = false;
    setCandidatesLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/admin/contracts", { params: { franchisee_id: franchiseeId } });
        if (cancelled) return;
        const rows = (data?.items || data || [])
          .filter((r) => r.status === "issued" || r.status === "signed")
          .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        setFranchiseeCandidates(rows);
        // Auto-pick the newest candidate so a lone predecessor doesn't
        // require an extra click.
        setPickedPredecessorId((prev) => prev && rows.some((r) => r.id === prev) ? prev : rows[0]?.id || null);
      } catch { /* silent — save-gate will surface */ }
      finally { if (!cancelled) setCandidatesLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [isRenewalTemplate, franchiseeId, renewalOf]);

  const effectiveRenewalOf = renewalOf
    || franchiseeCandidates.find((r) => r.id === pickedPredecessorId)
    || null;

  // Legacy-renewal path. When a renewal template is selected but no
  // supersedable Hub predecessor exists (the common case for
  // franchisees who signed under the old paper system), we still let
  // HQ save the draft as a renewal — it's just tagged with
  // ``renewal_origin: "legacy"`` and no ``supersedes_id`` is sent.
  // ``legacyAcknowledged`` auto-enables once we've confirmed there's
  // no Hub predecessor; HQ can also toggle it manually on the parent-
  // injected renewalOf path when they know the linked contract isn't
  // actually the one being renewed (e.g. a stale draft they want to
  // ignore).
  const hubPredecessorAvailable = Boolean(effectiveRenewalOf);
  const legacyAvailable = isRenewalTemplate && !hubPredecessorAvailable && !candidatesLoading;
  const [legacyAcknowledged, setLegacyAcknowledged] = useState(false);
  useEffect(() => {
    if (legacyAvailable) setLegacyAcknowledged(true);
    else if (!isRenewalTemplate) setLegacyAcknowledged(false);
  }, [legacyAvailable, isRenewalTemplate]);
  // Optional audit fields captured on legacy renewals so HQ retains a
  // paper trail without having to re-upload the old PDF.
  const [legacyPredecessorExpiry, setLegacyPredecessorExpiry] = useState("");
  const [legacyPredecessorReference, setLegacyPredecessorReference] = useState("");
  const [legacyPredecessorNotes, setLegacyPredecessorNotes] = useState("");
  const isLegacyRenewal = isRenewalTemplate && !hubPredecessorAvailable && legacyAcknowledged;

  // Renewal mode is ON when the template is a renewal type AND we
  // either have a Hub predecessor OR the user has acknowledged the
  // legacy-renewal path.
  const isRenewalMode = Boolean(effectiveRenewalOf)
    || isLegacyRenewal
    || Boolean(renewalOf);
  // Hard block ONLY when the user is claiming a Hub renewal (renewal
  // template + explicit predecessor selection would be required) but
  // the selected predecessor is invalid. A "template picked, no
  // predecessor" state is now a legitimate legacy-renewal path (see
  // ``isLegacyRenewal``) not a block condition.
  const renewalMissingPredecessor = false;

  // Legal name is now AUTO-RESOLVED at issuance from the franchisee's
  // First name + Last name. Leave this field blank — it's an optional
  // per-contract OVERRIDE for LLC / limited-company edge cases. Never
  // pre-fill from ``organisation`` because that silently made the
  // legal name and trading name resolve identically on the PDF.
  const [franchiseeLegalName, setFranchiseeLegalName] = useState("");
  const [dualEnabled, setDualEnabled] = useState(false);
  const [dualPdf, setDualPdf] = useState(null);
  const [dualNames, setDualNames] = useState(["", ""]);
  const [hqSignatoryName, setHqSignatoryName] = useState("");
  const [hqSignatoryTitle, setHqSignatoryTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  // Contract dates + term. All backed by ``contracts.*`` fields on
  // the draft doc and freezable via the CMS marker resolver
  // ({{COMMENCEMENT_DATE}}, {{RENEWAL_DATE}}, {{CONTRACT_TERM_YEARS}}).
  // Renewal drafts also take a renewal_fee; new drafts take an
  // initial_franchise_fee. All optional at draft time; issuance
  // validates whichever markers the template embeds.
  const [contractTermYears, setContractTermYears] = useState("");
  // ---------------------------------------------------------------
  // Commencement date is FULLY controlled by state and — critically —
  // is NEVER reset by any downstream effect (template change, term
  // change, renewal toggle, save & reopen, etc.). The initial
  // suggestion is:
  //   * For a renewal   → the day AFTER the predecessor's renewal_date
  //                       (i.e. the day after the previous expiry).
  //   * For a new draft → left blank so HQ picks explicitly.
  // A ``userEditedCommencement`` flag guards against re-suggesting
  // once the user has typed anything (or hit the quick-fill button).
  // The onChange guard also refuses to blank the state on a transient
  // invalid input so a mid-edit doesn't silently wipe a previously
  // valid date.
  // ---------------------------------------------------------------
  const suggestedCommencement = (() => {
    // For a Hub renewal, use the linked Hub predecessor's renewal_date.
    // For a legacy renewal, fall back to any legacy expiry date HQ has
    // entered as an audit field. Otherwise no suggestion (HQ types it).
    const prev = effectiveRenewalOf?.renewal_date || legacyPredecessorExpiry;
    if (!prev) return "";
    try {
      const d = new Date(prev);
      if (isNaN(d.getTime())) return "";
      // Renewal starts the DAY AFTER previous expiry.
      d.setDate(d.getDate() + 1);
      return d.toISOString().slice(0, 10);
    } catch { return ""; }
  })();
  const [commencementDate, setCommencementDate] = useState(suggestedCommencement);
  const [userEditedCommencement, setUserEditedCommencement] = useState(false);

  // Seed the commencement date when the resolved predecessor changes
  // (template select flips to a renewal, franchisee change, or the
  // picker chooses a different predecessor). Only re-seed when the
  // user hasn't manually edited yet — their pick always wins.
  useEffect(() => {
    if (userEditedCommencement) return;
    if (!suggestedCommencement) return;
    setCommencementDate(suggestedCommencement);
  }, [suggestedCommencement, userEditedCommencement]);
  const [renewalDate, setRenewalDate] = useState("");
  // Renewal fee (GBP, ex-VAT). Applies to BOTH new franchise contracts
  // and renewal contracts — populates `contracts.renewal_fee` on the
  // draft doc and drives the {{RENEWAL_FEE}} marker in the PDF.
  // Defaults to £500 for every fresh draft; HQ can amend before save.
  // We seed the default here (not just in the input's placeholder) so
  // that a HQ user who never touches the field still ends up with a
  // real value on the draft.
  const [renewalFee, setRenewalFee] = useState("500");
  // Auto-populate renewal_date from commencement + term when both
  // are set and HQ hasn't manually overridden it. Manual edits win —
  // once the user types in the renewal_date field we stop auto-syncing
  // (tracked by ``renewalDateManual``).
  const [renewalDateManual, setRenewalDateManual] = useState(false);
  useEffect(() => {
    if (renewalDateManual) return;
    if (!commencementDate || !contractTermYears) return;
    try {
      const d = new Date(commencementDate);
      if (isNaN(d.getTime())) return;
      const years = Number(contractTermYears);
      if (!Number.isFinite(years) || years <= 0) return;
      d.setFullYear(d.getFullYear() + years);
      const iso = d.toISOString().slice(0, 10);
      setRenewalDate(iso);
    } catch { /* ignore */ }
  }, [commencementDate, contractTermYears, renewalDateManual]);
  // Renewal mode is now derived from the resolved predecessor (see
  // ``isRenewalMode`` above) plus an explicit toggle the user can flip
  // OFF for the parent-injected ``renewalOf`` case (franchisee page)
  // to allow issuing a fresh new-franchise contract instead. The
  // template-driven renewal path (renewal template + auto-picker) is
  // NOT user-flippable — a renewal template must be tied to a
  // predecessor.
  const [renewalToggleOff, setRenewalToggleOff] = useState(false);
  const renewalOn = isRenewalMode && !renewalToggleOff;
  // Initial Franchise Fee (GBP, ex-VAT). Only surfaced when the
  // draft is NOT a renewal. Pre-populated from the franchisee's
  // canonical ``bought_for`` field when present so HQ just confirms
  // rather than re-typing a historic amount.
  const currentFr = franchiseeId
    ? franchisees.find((f) => f.id === franchiseeId)
    : lockedFr;
  const [initialFranchiseFee, setInitialFranchiseFee] = useState(() => {
    const v = currentFr?.bought_for;
    return v == null || v === "" ? "" : String(v);
  });
  // Keep the field in sync when the selected franchisee changes
  // (open modal from Admin Contracts page where the picker is live).
  useEffect(() => {
    const v = currentFr?.bought_for;
    setInitialFranchiseFee(v == null || v === "" ? "" : String(v));
  }, [franchiseeId, currentFr]);

  const franchiseesSorted = useMemo(() =>
    [...franchisees].sort((a, b) => {
      const an = `${a.first_name || ""} ${a.last_name || ""}`.trim();
      const bn = `${b.first_name || ""} ${b.last_name || ""}`.trim();
      return an.localeCompare(bn);
    }), [franchisees]);

  async function submit() {
    setSaving(true); setErr("");
    // Hard gate: renewal template requires a predecessor.
    if (renewalMissingPredecessor) {
      setErr(
        "This is a renewal template but no previous issued/signed contract was found for this franchisee. " +
        "Pick a franchisee who has a previous contract, or switch to a non-renewal template.",
      );
      setSaving(false);
      return;
    }
    try {
      const body = {
        template_id: templateId,
        franchisee_id: franchiseeId,
      };
      if (monthlyFee) body.monthly_fee = Number(monthlyFee);
      if (franchiseeLegalName) body.franchisee_legal_name = franchiseeLegalName;
      if (hqSignatoryName) body.hq_signatory_name = hqSignatoryName;
      if (hqSignatoryTitle) body.hq_signatory_title = hqSignatoryTitle;
      if (renewalOn && effectiveRenewalOf?.id) {
        body.supersedes_id = effectiveRenewalOf.id;
        body.renewal_origin = "hub";
      } else if (isLegacyRenewal) {
        // Renewal against a paper/pre-Hub agreement — no supersedes_id
        // gets set (there's no Hub row to link) but the draft is
        // clearly labelled as a renewal, not a new franchise contract.
        body.renewal_origin = "legacy";
        if (legacyPredecessorExpiry) body.legacy_predecessor_expiry_date = legacyPredecessorExpiry;
        if (legacyPredecessorReference) body.legacy_predecessor_reference = legacyPredecessorReference;
        if (legacyPredecessorNotes) body.legacy_predecessor_notes = legacyPredecessorNotes;
      }
      // Dates & term — always pass through when set. Backend accepts
      // ISO date strings; empty strings are dropped so a partially
      // filled draft can still be saved for later completion.
      if (contractTermYears) body.contract_term_years = Number(contractTermYears);
      if (commencementDate) {
        body.commencement_date = commencementDate;
        // Legacy alias — some template markers still reference the
        // older ``term_start_date`` field name. Keeping both in sync
        // avoids "missing value" issues at issuance for older PDFs.
        body.term_start_date = commencementDate;
      }
      if (renewalDate) body.renewal_date = renewalDate;
      // Initial Franchise Fee — only sent on non-renewal drafts, so
      // renewals never carry (or overwrite) the historic amount.
      const isRenewal = (renewalOn && effectiveRenewalOf?.id) || isLegacyRenewal;
      if (!isRenewal && initialFranchiseFee !== "") {
        body.initial_franchise_fee = Number(initialFranchiseFee);
      }
      // Renewal fee — always sent when the field is populated. Applies
      // to both new franchise and renewal contracts. Empty string is
      // still allowed (dropped from payload) if HQ deliberately clears
      // the default before saving.
      if (renewalFee !== "") {
        body.renewal_fee = Number(renewalFee);
      }
      if (dualEnabled) {
        if (!dualPdf || dualNames.some((name) => !name.trim())) {
          throw new Error("Upload the prepared PDF and enter both signatory names.");
        }
        const form = new FormData();
        form.append("metadata", JSON.stringify(body));
        form.append("signatories", JSON.stringify(dualNames.map((name) => name.trim())));
        form.append("pdf", dualPdf);
        await api.post("/admin/contracts/dual-upload", form);
      } else {
        await api.post("/admin/contracts", body);
      }
      await onCreated();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : JSON.stringify(d || e.message));
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="new-contract-modal">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold">
            {(renewalOn && effectiveRenewalOf) || isLegacyRenewal ? "Renew contract" : "New contract"}
          </h2>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-800" data-testid="new-contract-close-btn">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {/* Renewal indicator/toggle:
              * Parent-injected renewalOf (franchisee page) → user can flip
                renewal off to issue a fresh new-franchise contract instead.
              * Template-driven renewal (auto-picked predecessor) → shown as
                a locked amber banner + a predecessor picker if there are
                multiple candidates. */}
          {renewalOf && (
            <label className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 rounded p-2 cursor-pointer"
                   data-testid="new-contract-renewal-toggle-row">
              <input
                type="checkbox"
                checked={renewalOn}
                onChange={(e) => setRenewalToggleOff(!e.target.checked)}
                className="mt-0.5"
                data-testid="new-contract-renewal-toggle" />
              <span className="text-amber-900">
                <strong>Renewal</strong> — this draft supersedes
                <code className="mx-1 px-1 bg-white border border-amber-200 rounded">
                  {contractRef(renewalOf)}
                </code>
                when it is issued.
              </span>
            </label>
          )}
          {!renewalOf && isRenewalTemplate && effectiveRenewalOf && (
            <div className="text-xs bg-amber-50 border border-amber-300 rounded p-2.5 space-y-2"
                 data-testid="new-contract-renewal-auto-banner">
              <div className="flex items-start gap-2 text-amber-900">
                <span className="mt-0.5">🔗</span>
                <span>
                  <strong>Renewal template detected.</strong> This draft will supersede
                  <code className="mx-1 px-1 bg-white border border-amber-200 rounded">
                    {contractRef(effectiveRenewalOf)}
                  </code>
                  when it is issued.
                </span>
              </div>
              {franchiseeCandidates.length > 1 && (
                <label className="block text-xs text-amber-900">
                  <span className="uppercase tracking-wider font-bold text-[10px] block mb-1">
                    Superseding
                  </span>
                  <select
                    value={pickedPredecessorId || ""}
                    onChange={(e) => setPickedPredecessorId(e.target.value)}
                    className="w-full border border-amber-300 rounded px-2 py-1.5 text-sm bg-white"
                    data-testid="new-contract-predecessor-picker">
                    {franchiseeCandidates.map((r) => (
                      <option key={r.id} value={r.id}>
                        {contractRef(r)}
                        {r.commencement_date ? ` · commenced ${new Date(r.commencement_date).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/London" })}` : ""}
                        {" · "}{r.status}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          )}
          {legacyAvailable && (
            <div className="text-xs bg-amber-50 border border-amber-300 rounded p-2.5 space-y-2.5"
                 data-testid="new-contract-legacy-renewal-panel">
              <div className="flex items-start gap-2 text-amber-900">
                <span className="mt-0.5">📜</span>
                <span>
                  <strong>Legacy renewal.</strong> No previous Hub contract was found for this franchisee. This renewal will be recorded as following a legacy agreement from the previous contract system — no Hub contract will be marked superseded.
                </span>
              </div>
              <label className="flex items-start gap-2 text-[11px] text-amber-900 cursor-pointer">
                <input
                  type="checkbox"
                  checked={legacyAcknowledged}
                  onChange={(e) => setLegacyAcknowledged(e.target.checked)}
                  className="mt-0.5"
                  data-testid="new-contract-legacy-acknowledged" />
                <span>Previous agreement predates the Hub contract system.</span>
              </label>
              {isLegacyRenewal && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 border-t border-amber-200 pt-2.5">
                  <label className="block text-xs">
                    <span className="uppercase tracking-wider font-bold text-[10px] text-amber-900 block mb-1">
                      Legacy predecessor expiry (optional)
                    </span>
                    <input
                      type="date"
                      value={legacyPredecessorExpiry}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "" || /^\d{4}-\d{2}-\d{2}$/.test(v)) setLegacyPredecessorExpiry(v);
                      }}
                      className="w-full border border-amber-300 rounded px-2 py-1.5 text-xs bg-white"
                      data-testid="new-contract-legacy-expiry" />
                  </label>
                  <label className="block text-xs">
                    <span className="uppercase tracking-wider font-bold text-[10px] text-amber-900 block mb-1">
                      Legacy contract reference (optional)
                    </span>
                    <input
                      type="text"
                      value={legacyPredecessorReference}
                      onChange={(e) => setLegacyPredecessorReference(e.target.value)}
                      placeholder="e.g. Paper-2019-Paloma"
                      className="w-full border border-amber-300 rounded px-2 py-1.5 text-xs bg-white"
                      data-testid="new-contract-legacy-reference" />
                  </label>
                  <label className="block text-xs sm:col-span-2">
                    <span className="uppercase tracking-wider font-bold text-[10px] text-amber-900 block mb-1">
                      Legacy notes (optional — audit only)
                    </span>
                    <textarea
                      value={legacyPredecessorNotes}
                      onChange={(e) => setLegacyPredecessorNotes(e.target.value)}
                      rows={2}
                      placeholder="Any context about the previous paper agreement — HQ audit only."
                      className="w-full border border-amber-300 rounded px-2 py-1.5 text-xs bg-white"
                      data-testid="new-contract-legacy-notes" />
                  </label>
                </div>
              )}
            </div>
          )}
          {renewalMissingPredecessor && (
            <div className="text-xs bg-red-50 border border-red-300 rounded p-2.5 text-red-800"
                 data-testid="new-contract-renewal-missing-predecessor">
              ⚠️ <strong>Renewal template selected but no supersedable predecessor found.</strong>
              {candidatesLoading
                ? " Looking up the franchisee's contracts…"
                : franchiseeId
                  ? " This franchisee has no issued or signed contract to renew against. Pick a different franchisee, or switch to a non-renewal template."
                  : " Select a franchisee first."}
            </div>
          )}
          <label className="block text-sm">
            <span className="text-stone-700">Template</span>
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="w-full mt-1 border rounded-md px-2 py-1.5 text-sm"
              data-testid="new-contract-template-select">
              <option value="">— select template —</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-stone-700">Franchisee{lockedFranchiseeId && " (locked to this record)"}</span>
            <select
              value={franchiseeId}
              onChange={(e) => setFranchiseeId(e.target.value)}
              disabled={Boolean(lockedFranchiseeId)}
              className="w-full mt-1 border rounded-md px-2 py-1.5 text-sm disabled:bg-stone-100 disabled:text-stone-500"
              data-testid="new-contract-franchisee-select">
              <option value="">— select franchisee —</option>
              {franchiseesSorted.map((f) => (
                <option key={f.id} value={f.id}>
                  {`${f.first_name || ""} ${f.last_name || ""}`.trim() || f.organisation || f.id}
                  {f.organisation ? ` — ${f.organisation}` : ""}
                </option>
              ))}
            </select>
          </label>
          <fieldset className="border rounded-md p-3 space-y-3" data-testid="dual-signatories-section">
            <legend className="px-1 text-sm font-semibold">Dual Signatories</legend>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="dual-enabled-checkbox" checked={dualEnabled} onChange={(e) => setDualEnabled(e.target.checked)} />
              Use a manually prepared two-person contract
            </label>
            {dualEnabled && <>
              <p className="text-xs text-stone-600">The PDF must already contain all names, addresses, fees, dates and the territory link. The fields below are Hub records; they will not change your uploaded PDF.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="text-sm">Upload dual contract PDF
                  <input type="file" data-testid="dual-pdf-input" accept="application/pdf,.pdf" className="block w-full mt-1 text-xs" onChange={(e) => setDualPdf(e.target.files?.[0] || null)} />
                </label>
                {dualNames.map((name, index) => <label className="text-sm" key={index}>
                  {index === 0 ? "First signatory" : "Second signatory"}
                  <input value={name} data-testid={`dual-signatory-${index + 1}`} maxLength={100} onChange={(e) => setDualNames((old) => old.map((v, i) => i === index ? e.target.value : v))} className="w-full mt-1 border rounded-md px-2 py-1.5 text-sm" />
                </label>)}
              </div>
              <p className="text-xs text-stone-600">Match these names exactly to the PDF. Preview the saved draft before issuing; the preview shows example acceptance text and dates.</p>
            </>}
          </fieldset>
          <label className="block text-sm">
            <span className="text-stone-700">Monthly fee (£)</span>
            <input
              type="number" step="0.01" min="0"
              value={monthlyFee}
              onChange={(e) => setMonthlyFee(e.target.value)}
              className="w-full mt-1 border rounded-md px-2 py-1.5 text-sm"
              data-testid="new-contract-monthly-fee-input" />
          </label>
          {/* Initial Franchise Fee — first-contract only, ex-VAT.
              Renewal drafts never see this field so the historic
              amount recorded on the initial contract stays frozen. */}
          {!((renewalOn && effectiveRenewalOf?.id) || isLegacyRenewal) && (
            <label className="block text-sm" data-testid="new-contract-initial-franchise-fee-row">
              <span className="text-stone-700">Initial Franchise Fee (£)</span>
              <input
                type="number" step="0.01" min="0"
                value={initialFranchiseFee}
                onChange={(e) => setInitialFranchiseFee(e.target.value)}
                placeholder="e.g. 3500"
                className="w-full mt-1 border rounded-md px-2 py-1.5 text-sm"
                data-testid="new-contract-initial-franchise-fee-input" />
              <span className="block mt-1 text-[11px] text-stone-500">
                Enter the initial franchise purchase fee excluding VAT.
                {currentFr?.bought_for != null && currentFr?.bought_for !== "" && (
                  <> This franchisee&apos;s recorded &ldquo;Bought For&rdquo; value has been pre-filled — amend if the historic contract used a different figure.</>
                )}
              </span>
            </label>
          )}
          {/* Renewal fee — shown on BOTH new-franchise and renewal
              drafts. Sits directly beneath Initial Franchise Fee on
              new contracts; takes the equivalent position on renewals
              where the Initial Franchise Fee row is hidden. Populates
              `contracts.renewal_fee` on the draft doc, which the
              resolver reads verbatim into {{RENEWAL_FEE}} on the PDF. */}
          <label className="block text-sm" data-testid="new-contract-renewal-fee-row">
            <span className="text-stone-700">Renewal fee (£)</span>
            <input
              type="number" step="0.01" min="0"
              value={renewalFee}
              onChange={(e) => setRenewalFee(e.target.value)}
              placeholder="e.g. 500"
              className="w-full mt-1 border rounded-md px-2 py-1.5 text-sm"
              data-testid="new-contract-renewal-fee-input" />
            <span className="block mt-1 text-[11px] text-stone-500">
              One-off renewal fee at the end of the term (excluding VAT).
              Defaults to £500 — amend if this contract carries a different figure.
            </span>
          </label>
          {/* Dates & term — populates the ``{{COMMENCEMENT_DATE}}``,
              ``{{RENEWAL_DATE}}`` and ``{{CONTRACT_TERM_YEARS}}``
              markers on the PDF. Renewal-date auto-computes from
              commencement + term until HQ manually overrides it. */}
          <fieldset className="border border-stone-200 rounded-lg p-3 space-y-3" data-testid="new-contract-dates-fieldset">
            <legend className="px-1 text-[10px] uppercase tracking-widest font-bold text-stone-500">Dates &amp; term</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="text-stone-700">Commencement date</span>
                <input
                  type="date"
                  value={commencementDate}
                  onChange={(e) => {
                    const v = e.target.value;
                    // Only accept the change if it's a valid YYYY-MM-DD
                    // string or an intentional clear. This prevents a
                    // transient mid-edit blank in some browsers from
                    // silently wiping a previously-good date and letting
                    // the calendar picker roll back to today.
                    if (v === "" || /^\d{4}-\d{2}-\d{2}$/.test(v)) {
                      setCommencementDate(v);
                      setUserEditedCommencement(true);
                    }
                  }}
                  className="w-full mt-1 border rounded-md px-2 py-1.5 text-sm"
                  data-testid="new-contract-commencement-date-input" />
                {(renewalOn && effectiveRenewalOf?.renewal_date || (isLegacyRenewal && legacyPredecessorExpiry)) && !userEditedCommencement && suggestedCommencement && (
                  <button
                    type="button"
                    onClick={() => {
                      setCommencementDate(suggestedCommencement);
                      setUserEditedCommencement(true);
                    }}
                    className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-800 hover:text-amber-900 underline"
                    data-testid="new-contract-commencement-suggestion">
                    Use day after previous expiry ({new Date(suggestedCommencement).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/London" })})
                  </button>
                )}
                <span className="block mt-1 text-[11px] text-stone-500">
                  {renewalOn && effectiveRenewalOf?.renewal_date
                    ? `The day the renewal term begins. Previous expiry: ${new Date(effectiveRenewalOf.renewal_date).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/London" })}. Retrospective dates allowed. The PDF's AGREEMENT DATED will match this date.`
                    : isLegacyRenewal
                      ? "The day this renewal term begins. Retrospective dates allowed. The PDF's AGREEMENT DATED will match this date."
                      : <>The date the term starts. Also used as <code className="bg-stone-100 rounded px-1">term_start_date</code>.</>}
                </span>
              </label>
              <label className="block text-sm">
                <span className="text-stone-700">Term (years)</span>
                <input
                  type="number" min="1" max="20" step="1"
                  value={contractTermYears}
                  onChange={(e) => setContractTermYears(e.target.value)}
                  placeholder="e.g. 5"
                  className="w-full mt-1 border rounded-md px-2 py-1.5 text-sm"
                  data-testid="new-contract-term-years-input" />
                <span className="block mt-1 text-[11px] text-stone-500">
                  Length of the agreement in whole years.
                </span>
              </label>
              <label className="block text-sm sm:col-span-2">
                <span className="text-stone-700">Renewal / expiry date</span>
                <input
                  type="date"
                  value={renewalDate}
                  onChange={(e) => { setRenewalDate(e.target.value); setRenewalDateManual(true); }}
                  className="w-full mt-1 border rounded-md px-2 py-1.5 text-sm"
                  data-testid="new-contract-renewal-date-input" />
                <span className="block mt-1 text-[11px] text-stone-500">
                  {renewalDateManual
                    ? "Manual — auto-sync from commencement + term is disabled."
                    : "Auto-computed from Commencement + Term. Edit to override."}
                </span>
              </label>
            </div>
          </fieldset>
          <label className="block text-sm">
            <span className="text-stone-700">Franchisee legal name <span className="text-stone-400 font-normal">(optional override)</span></span>
            <input
              type="text"
              value={franchiseeLegalName}
              onChange={(e) => setFranchiseeLegalName(e.target.value)}
              placeholder={
                currentFr
                  ? `${currentFr.first_name || ""} ${currentFr.last_name || ""}`.trim() || "e.g. Paloma Ibarra"
                  : "e.g. Paloma Ibarra"
              }
              className="w-full mt-1 border rounded-md px-2 py-1.5 text-sm"
              data-testid="new-contract-legal-name-input" />
            <span className="block mt-1 text-[11px] text-stone-500">
              Leave blank to auto-resolve to the franchisee&apos;s <strong>First name + Last name</strong> on the Hub profile.
              Only enter a value here if the signing entity is a limited company or otherwise differs from the natural person&apos;s name.
              <strong> The trading / organisation name resolves separately via <code className="bg-stone-100 rounded px-1">[[FRANCHISEE_ORGANISATION]]</code>.</strong>
            </span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-stone-700">HQ signatory name</span>
              <input
                type="text"
                value={hqSignatoryName}
                onChange={(e) => setHqSignatoryName(e.target.value)}
                className="w-full mt-1 border rounded-md px-2 py-1.5 text-sm"
                data-testid="new-contract-hq-name-input" />
            </label>
            <label className="block text-sm">
              <span className="text-stone-700">HQ signatory title</span>
              <input
                type="text"
                value={hqSignatoryTitle}
                onChange={(e) => setHqSignatoryTitle(e.target.value)}
                className="w-full mt-1 border rounded-md px-2 py-1.5 text-sm"
                data-testid="new-contract-hq-title-input" />
            </label>
          </div>
          {err && (
            <div className="p-2 text-xs bg-red-50 border border-red-200 text-red-800 rounded flex items-center gap-1.5">
              <AlertTriangle className="h-3 w-3" /> {err}
            </div>
          )}
        </div>
        <div className="p-4 border-t bg-stone-50 rounded-b-lg flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm border rounded-md bg-white hover:bg-stone-100"
            data-testid="new-contract-cancel-btn">Cancel</button>
          <button
            onClick={submit}
            disabled={saving || !templateId || !franchiseeId || renewalMissingPredecessor}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-md bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            data-testid="new-contract-save-btn">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Save draft
          </button>
        </div>
      </div>
    </div>
  );
}
